import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { Bell, CheckCheck, Factory, Truck, Shield, Package, GitBranch, X, BellRing, Volume2, ArrowLeftRight } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";

const CATEGORY_ICONS = {
  workflow: GitBranch,
  production: Factory,
  quality: Shield,
  dispatch: Truck,
  purchase: Package,
  transfer: ArrowLeftRight,
  system: Bell,
};

const CATEGORY_COLORS = {
  workflow: "text-purple-500 bg-purple-50",
  production: "text-blue-500 bg-blue-50",
  quality: "text-emerald-500 bg-emerald-50",
  dispatch: "text-amber-500 bg-amber-50",
  purchase: "text-sky-500 bg-sky-50",
  transfer: "text-indigo-500 bg-indigo-50",
  system: "text-gray-500 bg-gray-50",
};

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const now = new Date();
  const then = new Date(dateStr);
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// Generate a subtle notification chime using Web Audio API
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  } catch {}
}

function fireBrowserNotification(n) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  try {
    const notif = new Notification(n.title, {
      body: n.message,
      icon: "/favicon.ico",
      tag: n.id,
      silent: true,
    });
    notif.onclick = () => {
      window.focus();
      if (n.link) window.location.hash = "";
      notif.close();
    };
    setTimeout(() => notif.close(), 8000);
  } catch {}
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [permissionState, setPermissionState] = useState("default");
  const [showPermBanner, setShowPermBanner] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem("notif_sound") !== "off");
  const panelRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const initialLoadRef = useRef(true);

  // Check browser notification support & permission
  useEffect(() => {
    if (typeof Notification !== "undefined") {
      setPermissionState(Notification.permission);
      if (Notification.permission === "default") {
        const dismissed = sessionStorage.getItem("notif_banner_dismissed");
        if (!dismissed) setShowPermBanner(true);
      }
    }
  }, []);

  const requestPermission = async () => {
    if (typeof Notification === "undefined") return;
    try {
      const result = await Notification.requestPermission();
      setPermissionState(result);
      setShowPermBanner(false);
    } catch {}
  };

  const dismissBanner = () => {
    setShowPermBanner(false);
    sessionStorage.setItem("notif_banner_dismissed", "1");
  };

  const toggleSound = () => {
    setSoundEnabled(prev => {
      const next = !prev;
      localStorage.setItem("notif_sound", next ? "on" : "off");
      return next;
    });
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get("/notifications?limit=20");
      const incoming = res.data.notifications || [];
      const newUnread = res.data.unread_count || 0;

      // Detect truly new notifications (not seen in previous polls)
      if (!initialLoadRef.current) {
        const newItems = incoming.filter(n => !n.is_read && !seenIdsRef.current.has(n.id));
        for (const n of newItems) {
          fireBrowserNotification(n);
        }
        if (newItems.length > 0 && soundEnabled) {
          playNotificationSound();
        }
      }

      // Update seen set
      incoming.forEach(n => seenIdsRef.current.add(n.id));
      initialLoadRef.current = false;

      setNotifications(incoming);
      setUnreadCount(newUnread);
    } catch {}
  }, [soundEnabled]);

  useEffect(() => {
    fetchNotifications();
    const iv = setInterval(fetchNotifications, 60000);
    return () => clearInterval(iv);
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success("All notifications marked as read");
    } catch (err) {
      console.error("Failed to mark all as read:", err);
      toast.error("Failed to mark notifications as read");
    }
  };

  const handleClick = (n) => {
    if (!n.is_read) markRead(n.id);
    if (n.link) {
      navigate(n.link);
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9 rounded-md relative"
        onClick={() => setOpen(!open)}
        data-testid="notification-bell-btn"
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 animate-in fade-in"
            data-testid="notification-unread-count"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div
          className="absolute right-0 top-11 w-[380px] max-h-[480px] bg-card border rounded-md shadow-xl z-50 overflow-hidden flex flex-col animate-in slide-in-from-top-2 fade-in duration-200"
          data-testid="notification-panel"
        >
          {/* Permission banner */}
          {showPermBanner && permissionState === "default" && (
            <div className="flex items-center gap-2.5 px-4 py-2.5 bg-blue-50 border-b border-blue-100" data-testid="notif-permission-banner">
              <BellRing className="w-4 h-4 text-blue-600 shrink-0" />
              <p className="text-[11px] text-blue-700 flex-1">Enable desktop notifications to get alerts in the background</p>
              <button
                onClick={requestPermission}
                className="text-[11px] font-semibold text-blue-700 bg-blue-100 hover:bg-blue-200 px-2.5 py-1 rounded-lg transition-colors"
                data-testid="enable-notifications-btn"
              >
                Enable
              </button>
              <button onClick={dismissBanner} className="text-blue-400 hover:text-blue-600">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">Notifications</h3>
              {unreadCount > 0 && (
                <span className="text-[10px] font-bold bg-red-500 text-white rounded-full px-1.5 py-0.5">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={toggleSound}
                className={`p-1 rounded-md transition-colors ${soundEnabled ? "text-primary hover:bg-muted" : "text-muted-foreground/40 hover:bg-muted"}`}
                title={soundEnabled ? "Sound on" : "Sound off"}
                data-testid="toggle-sound-btn"
              >
                <Volume2 className="w-3.5 h-3.5" />
              </button>
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[11px] text-primary hover:underline flex items-center gap-1 ml-1"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="w-3.5 h-3.5" />Mark all read
                </button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-lg ml-1"
                onClick={() => setOpen(false)}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* List */}
          <div className="overflow-y-auto flex-1" data-testid="notification-list">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
                <Bell className="w-8 h-8 opacity-20" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => {
                const Icon = CATEGORY_ICONS[n.category] || Bell;
                const color = CATEGORY_COLORS[n.category] || CATEGORY_COLORS.system;
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors border-b border-b-transparent last:border-b-0 ${
                      !n.is_read ? "bg-primary/[0.03]" : ""
                    }`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    {/* Icon */}
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${color}`}>
                      <Icon className="w-4 h-4" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-[13px] leading-tight ${!n.is_read ? "font-semibold" : "font-medium text-muted-foreground"}`}>
                          {n.title}
                        </p>
                        <span className="text-[10px] text-muted-foreground/60 shrink-0 mt-0.5">
                          {timeAgo(n.created_at)}
                        </span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                      {n.doc_number && (
                        <span className="inline-block mt-1 text-[10px] font-medium mono px-1.5 py-0.5 rounded bg-muted">
                          {n.doc_number}
                        </span>
                      )}
                    </div>

                    {/* Unread dot */}
                    {!n.is_read && (
                      <div className="w-2.5 h-2.5 rounded-full bg-blue-500 shrink-0 mt-1.5" />
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t bg-muted/20 flex items-center justify-between">
            <button
              onClick={() => { navigate("/alert-center"); setOpen(false); }}
              className="text-[11px] font-medium text-primary hover:underline flex items-center gap-1"
              data-testid="view-all-alerts-btn"
            >
              View All Alerts
              <ArrowLeftRight className="w-3 h-3" />
            </button>
            {permissionState === "granted" && (
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span className="text-[10px] text-muted-foreground">Desktop active</span>
              </span>
            )}
            {permissionState === "denied" && (
              <span className="text-[10px] text-muted-foreground">Desktop blocked</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
